import type { SupabaseClient } from "@supabase/supabase-js";

import { humanizeLabel } from "../data/alert-queue";
import { cadRealtimeFeed } from "../data/cad-feed";
import { isOutsideSiteBoundary } from "../data/geofence-status";
import {
  DISABLED_DUTY_PRESENCE,
  dutyDisplayState,
  fetchDutyPresence,
  type DutyDisplayState,
  type DutyPing,
  type DutyPresenceSnapshot,
  type DutySession,
} from "../data/duty-pings";
import { fetchRoster, type RosterRow } from "../data/roster";
import { useReadModel } from "../data/useReadModel";
import { formatDisplayTime, formatRelativeTime } from "../lib/display-time";

function rosterLocationLine(row: RosterRow): string {
  const site = row.siteName?.trim() ?? "";
  const route = row.routeName?.trim() ?? "";
  if (site.length > 0 && route.length > 0 && site !== route) {
    return `${site} · ${route}`;
  }
  return site || route || "No route";
}

function assignmentContextLabel(status: string): string {
  if (status === "active") {
    return "On duty";
  }
  if (status === "scheduled") {
    return "Upcoming";
  }
  return humanizeLabel(status);
}

// On-shift roster surface (read-only) — who is scheduled/active right now, with redacted guard
// labels and shift start. When duty tracking is enabled for the account (PRD-001J), active rows
// also carry a duty-ping freshness chip: live / stale (>2× the policy interval) / no ping. Duty
// presence is an opt-in read (showDutyPresence) so the CAD only pulls pings when the viewer holds
// schedule.manage and the account policy is on. Coverage dials are a later enhancement.
interface OnShiftRosterProps {
  client: SupabaseClient;
  accountId: string;
  timeZone: string;
  pollMs?: number;
  enableRealtime?: boolean;
  /** Pull duty-ping freshness for active guards (PRD-001J J3). Default off. */
  showDutyPresence?: boolean;
  selectedRouteAssignmentId?: string | null;
  onSelectRouteAssignment?: (routeAssignmentId: string) => void;
}

export function OnShiftRoster(props: OnShiftRosterProps) {
  // Clear roster identity and duty freshness before reads for a newly selected workspace begin.
  return <AccountScopedOnShiftRoster key={props.accountId} {...props} />;
}

function AccountScopedOnShiftRoster({
  client,
  accountId,
  timeZone,
  pollMs = 30000,
  enableRealtime = true,
  showDutyPresence = false,
  selectedRouteAssignmentId = null,
  onSelectRouteAssignment,
}: OnShiftRosterProps) {
  const { status, data, error } = useReadModel<RosterRow[]>(
    () => fetchRoster(client, accountId),
    [client, accountId],
    {
      pollMs,
      ...(enableRealtime
        ? { realtime: cadRealtimeFeed(client, accountId, "roster", "route_assignments") }
        : {}),
    },
  );

  const duty = useReadModel<DutyPresenceSnapshot>(
    () => (showDutyPresence ? fetchDutyPresence(client, accountId) : Promise.resolve(DISABLED_DUTY_PRESENCE)),
    [client, accountId, showDutyPresence],
    { pollMs },
  );
  const dutyView = duty.data ?? DISABLED_DUTY_PRESENCE;

  if (status === "loading" && data === null) {
    return <p className="surface__state" role="status">Loading roster…</p>;
  }
  if (status === "error") {
    return (
      <p className="surface__state surface__state--error" role="alert">
        {error ?? "Could not load the roster."}
      </p>
    );
  }

  const rows = data ?? [];
  if (rows.length === 0) {
    return (
      <div className="surface__empty" role="status">
        <p className="surface__state">Nobody is on shift.</p>
        <p className="surface__state surface__state--quiet">
          If that looks wrong, the shift may never have been published. Open Schedule to check.
        </p>
      </div>
    );
  }

  const selectable = typeof onSelectRouteAssignment === "function";

  return (
    <>
      {showDutyPresence && duty.status === "error" ? (
        <p className="surface__state surface__state--error roster__duty-error" role="alert">
          {duty.error ?? "Duty pings are unavailable."}
        </p>
      ) : null}
      <ul className="roster roster--compact">
        {rows.map((row, index) => {
          const key = row.routeAssignmentId || `${row.guardLabel ?? "guard"}-${index}`;
          const selected = selectedRouteAssignmentId === row.routeAssignmentId;
          const showDutyChip = showDutyPresence && duty.status === "ready" && duty.data !== null;
          const session = dutyView.sessionsByRouteAssignment[row.routeAssignmentId];
          const sinceLabel =
            row.startsAt !== null ? `Since ${formatDisplayTime(row.startsAt, timeZone)}` : undefined;
          const itemClass = [
            "roster__item",
            selectable ? "roster__item--selectable" : "",
            selected ? "roster__item--selected" : "",
          ]
            .filter(Boolean)
            .join(" ");

          const inner = (
            <>
              <div className="roster__top">
                {row.assignmentStatus === "active" ? (
                  <span className="so-live-dot" aria-hidden="true" />
                ) : null}
                <span className="roster__guard">{row.guardLabel ?? "Guard"}</span>
                <span className={`roster__context roster__context--${row.assignmentStatus}`}>
                  {assignmentContextLabel(row.assignmentStatus)}
                </span>
              </div>
              <div className="roster__meta">
                <span className="roster__route" title={rosterLocationLine(row)}>
                  {rosterLocationLine(row)}
                </span>
                {!showDutyChip ? (
                  <span className={`roster__status roster__status--${row.assignmentStatus}`}>
                    {humanizeLabel(row.assignmentStatus)}
                  </span>
                ) : null}
              </div>
              {showDutyChip ? (
                <DutyPresenceChip
                  compact
                  ping={dutyView.pingsByRouteAssignment[row.routeAssignmentId]}
                  session={session}
                  assignmentStatus={row.assignmentStatus}
                  policyEnabled={dutyView.enabled}
                  requireClockIn={dutyView.requireClockIn}
                  intervalSeconds={dutyView.intervalSeconds}
                  timeZone={timeZone}
                  outsideSite={
                    dutyView.geofenceAlertsEnabled &&
                    isOutsideSiteBoundary(dutyView.geofenceByRouteAssignment[row.routeAssignmentId])
                  }
                />
              ) : null}
              {sinceLabel && !showDutyChip ? (
                <div className="roster__when">{sinceLabel}</div>
              ) : null}
            </>
          );

          return (
            <li key={key}>
              {selectable ? (
                <button
                  type="button"
                  className={itemClass}
                  onClick={() => onSelectRouteAssignment?.(row.routeAssignmentId)}
                  aria-pressed={selected}
                  title={sinceLabel}
                >
                  {inner}
                </button>
              ) : (
                <div className={itemClass}>{inner}</div>
              )}
            </li>
          );
        })}
      </ul>
    </>
  );
}

const DUTY_CHIP_COPY: Record<"fresh" | "stale" | "missing", string> = {
  fresh: "DUTY: LIVE",
  stale: "DUTY: STALE",
  missing: "DUTY: NO PING",
};

function dutyDetailCopy(
  display: DutyDisplayState,
  ping: DutyPing | undefined,
  session: DutySession | undefined,
  timeZone: string,
): string {
  if (display.reason === "live" || display.reason === "stale") {
    const pingAt = ping?.serverReceivedAt ?? ping?.deviceTime;
    return `Last ping ${formatRelativeTime(pingAt, Date.now(), timeZone)}.`;
  }
  if (display.reason === "policy_off") {
    return "Pings dark: duty tracking is off.";
  }
  if (display.reason === "no_clock_in") {
    return "No clock-in: tracking has not started.";
  }
  if (display.reason === "duty_off") {
    if (session?.lastPunchType === "break_start") {
      return session.lastPunchAt
        ? `Duty off: break started ${formatRelativeTime(session.lastPunchAt, Date.now(), timeZone)}.`
        : "Duty off: guard is on break.";
    }
    return session?.lastPunchAt
      ? `Duty off: clocked out ${formatRelativeTime(session.lastPunchAt, Date.now(), timeZone)}.`
      : "Duty off: no active duty session.";
  }
  return "Pings dark: no duty location has arrived.";
}

/** Per-guard duty-ping freshness. Stale = last ping older than 2× the policy interval. */
function DutyPresenceChip({
  ping,
  session,
  assignmentStatus,
  policyEnabled,
  requireClockIn,
  intervalSeconds,
  timeZone,
  compact = false,
  outsideSite = false,
}: {
  ping: DutyPing | undefined;
  session: DutySession | undefined;
  assignmentStatus: string;
  policyEnabled: boolean;
  requireClockIn: boolean;
  intervalSeconds: number;
  timeZone: string;
  compact?: boolean;
  outsideSite?: boolean;
}) {
  const display = dutyDisplayState({
    policyEnabled,
    requireClockIn,
    assignmentStatus,
    session,
    ping,
    nowMs: Date.now(),
    intervalSeconds,
  });
  const visibleOutsideSite = display.trackingActive && ping !== undefined && outsideSite;
  const alert = display.state !== "fresh" || visibleOutsideSite;
  return (
    <div className={`roster__duty roster__duty--${display.state}${compact ? " roster__duty--compact" : ""}`}>
      {visibleOutsideSite ? (
        <span className="roster__geofence-outside" role="status">
          Outside site
        </span>
      ) : null}
      <span className="roster__duty-badge" role={alert ? "status" : undefined}>
        {DUTY_CHIP_COPY[display.state]}
      </span>
      <span className="roster__duty-when">{dutyDetailCopy(display, ping, session, timeZone)}</span>
    </div>
  );
}
